import { useState } from "react";
import { useQuery } from "@apollo/client";
import getUserByIdQuery from "./graphql/query/getUserById.query";
// @ts-ignore
const jwt_decode = require("jwt-decode");

interface MyJwtPayload {
  user_id: number;
  exp?: number; // JWT exp claim (seconds since epoch)
}

/** Returns true if the JWT is expired or invalid. */
function isTokenExpired(token: string): boolean {
  try {
    const decoded = jwt_decode.jwtDecode(token) as MyJwtPayload;
    if (decoded.exp == null) return true;
    // exp is in seconds; compare with current time in seconds (with 60s buffer)
    return decoded.exp < Date.now() / 1000 - 60;
  } catch {
    return true;
  }
}

export function useToken() {
  const getToken = () => {
    let tokenString: string | null = localStorage.getItem("token");
    if (!tokenString) {
      return null;
    }
    if (isTokenExpired(tokenString)) {
      localStorage.removeItem("token");
      return null;
    }
    return tokenString;
  };

  const [token, setToken] = useState(getToken());

  const saveToken = (jwtToken: string) => {
    localStorage.setItem("token", jwtToken);
    setToken(jwtToken);
  };

  const removeToken = () => {
    localStorage.removeItem("token");
    setToken(null);
  };

  return {
    setToken: saveToken,
    removeToken,
    token,
  };
}

export function useCurrentUser() {
  const token = localStorage.getItem("token");
  if (!token || isTokenExpired(token)) return null;

  try {
    const decoded = jwt_decode.jwtDecode(token) as MyJwtPayload;
    return { id: decoded.user_id };
  } catch (error) {
    console.error("Failed to decode JWT during login:", error);
    return null;
  }
}

/**
 * Fetches the current user's profile (via the shared getUserById query) and
 * surfaces verification state alongside the decoded id.
 *
 * - `loading` is true only when the request is in flight AND we don't already
 *   have cached data for this user.
 * - `emailVerified` is strictly `boolean | null`. It's null while we don't
 *   know yet (no token / query hasn't resolved). Components should treat
 *   `null` as "don't show the unverified UI yet" to avoid a flicker.
 * - `refetch` triggers a network refresh after the user completes the PIN
 *   flow so the banner / gated CTAs update without a page reload.
 */
export function useCurrentUserProfile() {
  const user = useCurrentUser();
  const { data, loading, refetch } = useQuery(getUserByIdQuery, {
    variables: { userId: user?.id },
    skip: !user?.id,
    fetchPolicy: "cache-first",
  });

  const profile = data?.userById ?? null;
  const emailVerified: boolean | null =
    profile && typeof profile.emailVerified === "boolean" ? profile.emailVerified : null;

  return {
    id: user?.id ?? null,
    emailVerified,
    profile,
    loading: loading && !profile,
    refetch,
  };
}

export function currencyFormat(num: number) {
  return "$" + num.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
}

export type FixMeLater = any;

// TODO: Update with way to extract logged in user ID
export const CURRENT_USER_ID = 3;

export function formatImageURL(key?: string) {
  if (!key) return '';
  
  // If key is already a full URL, return it as-is
  if (key.startsWith('http://') || key.startsWith('https://')) {
    return key;
  }
  
  // Otherwise, construct S3 URL from the key
  const bucket = process.env.REACT_APP_S3_BUCKET || 's3-drivenow';
  const region = process.env.REACT_APP_REGION || 'us-east-2';
  return `https://${bucket}.s3.${region}.amazonaws.com/${key}`;
}

// Seed listings store S3-style keys in image_loc (drivenow_stock.png,
// placeholder_listing.jpg). Both resolve to the bundled stock photo — the
// legacy placeholder_listing.jpg key pointed at the logo, not a car image.
const STOCK_PLACEHOLDER_KEYS = ['placeholder_listing.jpg', 'drivenow_stock.png'] as const;
const STOCK_LISTING_IMAGE = '/images/drivenow_stock.png';
/** Fallback when image_loc is empty; use the same stock photo as seeded listings. */
export const LISTING_IMAGE_FALLBACK = STOCK_LISTING_IMAGE;

function keyIsStockPlaceholder(key: string): boolean {
  if (!key) return false;
  for (const k of STOCK_PLACEHOLDER_KEYS) {
    if (key === k || key.endsWith(`/${k}`)) return true; // key or full S3 URL form
  }
  return false;
}

/**
 * Resolve a listing image key into a displayable URL, swapping seeded stock
 * keys for the bundled drivenow_stock.png asset (no S3 upload required).
 *
 * - Returns the stock photo when `key` is missing or a known seed placeholder.
 * - Leaves user-uploaded (non-placeholder) images alone (S3 URLs).
 */
export function resolveListingImageUrl(
  key?: string | null,
  _opts: { darkMode?: boolean } = {}
): string {
  if (!key || keyIsStockPlaceholder(key)) {
    return STOCK_LISTING_IMAGE;
  }
  return formatImageURL(key);
}

/** Parse a listing `imageLoc` into non-empty string keys (order preserved). */
export function parseListingImageKeys(imageLoc: unknown): string[] {
  try {
    if (!imageLoc) return [];
    let images: unknown;
    if (typeof imageLoc === "string") {
      try {
        images = JSON.parse(imageLoc);
      } catch {
        images = [imageLoc];
      }
    } else if (Array.isArray(imageLoc)) {
      images = imageLoc;
    } else {
      images = imageLoc;
    }
    if (!Array.isArray(images)) images = [images];
    return (images as unknown[]).filter(
      (img): img is string => typeof img === "string" && img.trim().length > 0
    );
  } catch {
    return [];
  }
}

export function resolveFirstListingImage(
  imageLoc: unknown,
  opts: { darkMode?: boolean } = {}
): string {
  const keys = parseListingImageKeys(imageLoc);
  return keys.length > 0
    ? resolveListingImageUrl(keys[0], opts)
    : resolveListingImageUrl(null, opts);
}

/** Resolve every listing photo key to a displayable URL. */
export function resolveListingImageUrls(
  imageLoc: unknown,
  opts: { darkMode?: boolean } = {}
): string[] {
  const keys = parseListingImageKeys(imageLoc);
  if (keys.length === 0) return [resolveListingImageUrl(null, opts)];
  return keys.map((key) => resolveListingImageUrl(key, opts));
}

export function formatMileage(mileage?: string) {
  if (!mileage) return "(mileage NA)";
  const num = parseInt(mileage);
  if (num < 300) return `${300} miles`;
  return `${(num / 1000).toFixed(0)}k miles`;
}

/**
 * Normalizes capitalization for vehicle names (make, model, trim)
 * Capitalizes first letter of each word, but preserves common acronyms
 * Examples: "AUDI" -> "Audi", "BMW" -> "BMW", "F-150" -> "F-150"
 */
export function normalizeVehicleName(name?: string | null): string {
  if (!name) return "";
  
  // Common acronyms that should stay uppercase
  const acronyms = ["BMW", "SUV", "VIN", "GPS", "ABS", "ESP", "AWD", "FWD", "RWD"];
  
  // Check if the entire string is an acronym
  const upperName = name.toUpperCase();
  if (acronyms.includes(upperName)) {
    return upperName;
  }
  
  // Split by spaces, hyphens, and other separators, but preserve them
  return name
    .split(/(\s+|-+)/)
    .map((part, index) => {
      // Skip separators (spaces, hyphens)
      if (/^\s*$|^-+$/.test(part)) {
        return part;
      }
      
      // Check if this part is an acronym
      const upperPart = part.toUpperCase();
      if (acronyms.includes(upperPart)) {
        return upperPart;
      }
      
      // Capitalize first letter, lowercase the rest
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join("");
}

/**
 * Formats a vehicle title with normalized capitalization
 */
export function formatVehicleTitle(
  year?: number | null,
  make?: string | null,
  model?: string | null,
  trim?: string | null
): string {
  const parts = [
    year?.toString(),
    normalizeVehicleName(make),
    normalizeVehicleName(model),
    trim ? normalizeVehicleName(trim) : null,
  ].filter(Boolean);
  
  return parts.join(" ");
}

/** Trim labels that identify a vehicle no better than its model does. */
const GENERIC_TRIM_LABELS = new Set([
  "",
  "-",
  "base",
  "standard",
  "base / standard",
  "n/a",
  "na",
]);

export function isGenericTrimLabel(trim?: string | null): boolean {
  if (trim == null) return true;
  return GENERIC_TRIM_LABELS.has(String(trim).trim().toLowerCase());
}

export type ListingTitleFields = {
  identityDisplayTitle?: string | null;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
};

/**
 * Prefer the server-stamped canonical title (includes engine when trim is
 * generic). Fall back to the client formatter for legacy listings.
 */
export function resolveListingTitle(
  listing?: ListingTitleFields | null
): string {
  const stamped = listing?.identityDisplayTitle?.trim();
  if (stamped) return stamped;
  return formatVehicleTitle(
    listing?.year,
    listing?.make,
    listing?.model,
    listing?.trim
  );
}